import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Loader2 } from 'lucide-react'
import type { TipoCampo, CampoFormulario } from '../../types'

const TIPOS_CAMPO: { valor: TipoCampo; label: string }[] = [
  { valor: 'text', label: 'Texto' },
  { valor: 'email', label: 'Correo electrónico' },
  { valor: 'number', label: 'Número' },
  { valor: 'tel', label: 'Teléfono' },
  { valor: 'url', label: 'URL' },
  { valor: 'select', label: 'Selección' },
  { valor: 'provincias_ecuador', label: 'Provincias de Ecuador' },
  { valor: 'ciudades_ecuador', label: 'Ciudades de Ecuador' },
  { valor: 'checkbox', label: 'Casilla de verificación' },
  { valor: 'multi_step_checkbox', label: 'Checkbox con texto' },
  { valor: 'multi_step_checkbox_select', label: 'Checkbox con selección' },
  { valor: 'textarea', label: 'Área de texto' },
  { valor: 'date', label: 'Fecha' },
  { valor: 'checkbox_radio', label: 'Radio buttons' },
  { valor: 'checkbox_multiple', label: 'Checkbox múltiple' },
]

const TIPOS_CON_OPCIONES = ['select', 'checkbox_radio', 'checkbox_multiple', 'multi_step_checkbox_select']

interface FormData {
  nombre_campo: string
  etiqueta: string
  tipo_campo: TipoCampo
  placeholder: string
  requerido: boolean
  opciones_texto: string
}

interface CampoDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (data: {
    nombre_campo: string
    etiqueta: string
    tipo_campo: TipoCampo
    placeholder?: string
    requerido?: boolean
    configuracion_extra?: Record<string, unknown>
  }) => void
  isSubmitting: boolean
  campo?: CampoFormulario | null
  pasoId: number
}

export function CampoDialog({ open, onOpenChange, onSubmit, isSubmitting, campo, pasoId: _pasoId }: CampoDialogProps) {
  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm<FormData>({
    defaultValues: {
      nombre_campo: '',
      etiqueta: '',
      tipo_campo: 'text',
      placeholder: '',
      requerido: false,
      opciones_texto: '',
    },
  })

  const tipoCampo = watch('tipo_campo')

  useEffect(() => {
    if (open && campo) {
      const opciones = campo.configuracion_extra?.opciones_select as string[] | undefined
      reset({
        nombre_campo: campo.nombre_campo,
        etiqueta: campo.etiqueta,
        tipo_campo: campo.tipo_campo,
        placeholder: campo.placeholder || '',
        requerido: campo.requerido,
        opciones_texto: opciones?.join('\n') || '',
      })
    } else if (open) {
      reset({
        nombre_campo: '',
        etiqueta: '',
        tipo_campo: 'text',
        placeholder: '',
        requerido: false,
        opciones_texto: '',
      })
    }
  }, [open, campo, reset])

  const handleFormSubmit = (data: FormData) => {
    const nombreCampo = data.nombre_campo || data.etiqueta.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    const configExtra: Record<string, unknown> = {}

    if (TIPOS_CON_OPCIONES.includes(data.tipo_campo) && data.opciones_texto) {
      configExtra.opciones_select = data.opciones_texto.split('\n').map((o) => o.trim()).filter(Boolean)
    }

    onSubmit({
      nombre_campo: nombreCampo,
      etiqueta: data.etiqueta,
      tipo_campo: data.tipo_campo,
      placeholder: data.placeholder || undefined,
      requerido: data.requerido,
      configuracion_extra: Object.keys(configExtra).length > 0 ? configExtra : undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{campo ? 'Editar Campo' : 'Nuevo Campo'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label>Tipo de campo</Label>
            <Select value={tipoCampo} onValueChange={(v) => setValue('tipo_campo', v as TipoCampo)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIPOS_CAMPO.map((t) => (
                  <SelectItem key={t.valor} value={t.valor}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Etiqueta *</Label>
            <Input {...register('etiqueta', { required: 'Requerido' })} placeholder="Ej: Nombre completo" />
            {errors.etiqueta && <p className="text-sm text-destructive">{errors.etiqueta.message}</p>}
          </div>

          <div className="space-y-2">
            <Label>Nombre campo (auto si vacío)</Label>
            <Input {...register('nombre_campo')} placeholder="nombre_completo" />
          </div>

          <div className="space-y-2">
            <Label>Placeholder</Label>
            <Input {...register('placeholder')} placeholder="Texto de ejemplo" />
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="requerido"
              checked={watch('requerido')}
              onCheckedChange={(v) => setValue('requerido', !!v)}
            />
            <Label htmlFor="requerido">Campo requerido</Label>
          </div>

          {TIPOS_CON_OPCIONES.includes(tipoCampo) && (
            <div className="space-y-2">
              <Label>Opciones (una por línea)</Label>
              <Textarea
                {...register('opciones_texto')}
                placeholder={"Opción 1\nOpción 2\nOpción 3"}
                rows={5}
              />
            </div>
          )}

          {(tipoCampo === 'provincias_ecuador' || tipoCampo === 'ciudades_ecuador') && (
            <p className="text-sm text-muted-foreground">
              Este campo se configura automáticamente con datos de Ecuador.
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" disabled={isSubmitting} className="bg-teal-800 hover:bg-teal-900 text-white">
              {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {campo ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
